import { useEffect, useMemo, useState } from "react";
import {
  Loader2,
  MessageSquare,
  Send,
  StickyNote,
  Trash2,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

function initialsFrom(name = "") {
  const src = (name || "?").trim();
  if (!src) return "?";
  const parts = src.split(/\s+/).filter(Boolean);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function formatDateTimeHu(value) {
  if (!value) return "—";
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "—";
    return new Intl.DateTimeFormat("hu-HU", {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    }).format(d);
  } catch (e) {
    return "—";
  }
}

function relativeTimeHu(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "épp most";
  if (diff < 3600) return `${Math.floor(diff / 60)} perce`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} órája`;
  if (diff < 604800) return `${Math.floor(diff / 86400)} napja`;
  return formatDateTimeHu(value);
}

export function NotesTab({ employeeId }) {
  const { user } = useAuth();
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState("");
  const [posting, setPosting] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const fetchNotes = async () => {
    try {
      const { data } = await api.get(`/employees/${employeeId}/notes`);
      setNotes(data);
    } catch (e) {
      toast.error(
        formatApiError(e?.response?.data?.detail) ||
          "Nem sikerült betölteni a jegyzeteket."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!employeeId) return;
    fetchNotes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeId]);

  const sorted = useMemo(
    () =>
      [...notes].sort(
        (a, b) => new Date(b.created_at) - new Date(a.created_at)
      ),
    [notes]
  );

  const submit = async () => {
    const value = text.trim();
    if (!value) return;
    setPosting(true);
    try {
      const { data } = await api.post(`/employees/${employeeId}/notes`, {
        text: value,
      });
      setNotes((prev) => [...prev, data]);
      setText("");
    } catch (e) {
      toast.error(
        formatApiError(e?.response?.data?.detail) || "Nem sikerült elküldeni."
      );
    } finally {
      setPosting(false);
    }
  };

  const remove = async (note) => {
    if (!window.confirm("Biztosan törlöd ezt a jegyzetet?")) return;
    setDeletingId(note._id);
    try {
      await api.delete(`/employees/notes/${note._id}`);
      setNotes((prev) => prev.filter((n) => n._id !== note._id));
      toast.success("Jegyzet törölve.");
    } catch (e) {
      toast.error(
        formatApiError(e?.response?.data?.detail) || "Nem sikerült törölni."
      );
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <section
      data-testid="employee-notes-timeline"
      className="rounded-xl border border-border bg-[hsl(var(--sipx-elevated))]"
    >
      <header className="flex items-center justify-between border-b border-border px-5 py-3">
        <h3 className="text-[11px] font-medium uppercase tracking-[0.16em] text-muted-foreground">
          Belső jegyzetek · {sorted.length} bejegyzés
        </h3>
        <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground">
          <StickyNote className="h-3 w-3" strokeWidth={1.75} />
          Csak belső munkatársak látják
        </span>
      </header>

      {/* Composer */}
      <div className="border-b border-border px-5 py-4">
        <div className="flex items-start gap-3">
          <div className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-foreground text-background text-[10px] font-semibold">
            {initialsFrom(user?.name || user?.email)}
          </div>
          <div className="flex-1 space-y-2">
            <Textarea
              data-testid="employee-note-composer-textarea"
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Írj egy belső jegyzetet a munkavállalóhoz…"
              rows={2}
              className="resize-none"
              onKeyDown={(e) => {
                if (
                  e.key === "Enter" &&
                  (e.metaKey || e.ctrlKey) &&
                  text.trim()
                ) {
                  e.preventDefault();
                  submit();
                }
              }}
            />
            <div className="flex items-center justify-between gap-2">
              <span className="text-[11px] text-muted-foreground">
                Ctrl/⌘ + Enter = küldés
              </span>
              <Button
                size="sm"
                type="button"
                onClick={submit}
                disabled={posting || !text.trim()}
                data-testid="employee-note-composer-submit"
              >
                {posting ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Send className="h-3.5 w-3.5" />
                )}
                Jegyzet hozzáadása
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Timeline */}
      <div className="px-5 py-4">
        {loading ? (
          <div className="flex items-center justify-center py-10 text-sm text-muted-foreground">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Betöltés…
          </div>
        ) : sorted.length === 0 ? (
          <div className="flex flex-col items-center gap-3 px-6 py-10 text-center">
            <div className="grid h-10 w-10 place-items-center rounded-full border border-border bg-background">
              <MessageSquare
                className="h-4 w-4 text-muted-foreground"
                strokeWidth={1.75}
              />
            </div>
            <p className="text-sm font-medium">Még nincs jegyzet</p>
            <p className="text-xs text-muted-foreground">
              Adj hozzá egy belső jegyzetet — a kollégák itt látják majd a bejegyzéseket.
            </p>
          </div>
        ) : (
          <ol className="relative space-y-5">
            <span
              aria-hidden
              className="absolute left-[14px] top-2 bottom-2 w-px bg-border"
            />
            {sorted.map((note) => {
              const isOwn = note.created_by === user?._id;
              return (
                <li
                  key={note._id}
                  data-testid={`employee-note-${note._id}`}
                  className="relative pl-10"
                >
                  <span className="absolute left-0 top-0 grid h-7 w-7 place-items-center rounded-full border-2 border-[hsl(var(--sipx-elevated))] bg-foreground/90 text-background">
                    <span className="text-[10px] font-semibold">
                      {initialsFrom(note.created_by_name)}
                    </span>
                  </span>
                  <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 pb-1">
                    <span className="text-[12.5px] font-medium">
                      {note.created_by_name || "Ismeretlen"}
                    </span>
                    <span
                      className="text-[11px] text-muted-foreground"
                      title={formatDateTimeHu(note.created_at)}
                    >
                      · {relativeTimeHu(note.created_at)}
                    </span>
                    {isOwn && (
                      <button
                        type="button"
                        data-testid={`employee-note-delete-${note._id}`}
                        onClick={() => remove(note)}
                        disabled={deletingId === note._id}
                        className="ml-auto inline-flex items-center gap-1 text-[11px] text-muted-foreground transition-colors hover:text-destructive disabled:opacity-50"
                      >
                        {deletingId === note._id ? (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                          <Trash2 className="h-3 w-3" strokeWidth={1.75} />
                        )}
                        Törlés
                      </button>
                    )}
                  </div>
                  <p className="whitespace-pre-line text-sm leading-relaxed">
                    {note.text}
                  </p>
                </li>
              );
            })}
          </ol>
        )}
      </div>
    </section>
  );
}
